import { FitAddon } from '@xterm/addon-fit'
import { Terminal } from '@xterm/xterm'
import '@xterm/xterm/css/xterm.css'
import { useEffect, useRef } from 'react'

/**
 * O fundo do terminal, exportado porque a ABA ativa usa a mesma cor.
 *
 * A aba e o terminal precisam ler como uma peca so — a aba ativa emenda no
 * painel abaixo dela. Duas constantes iguais em arquivos diferentes divergem
 * na primeira edicao de uma delas, entao ha uma so, e mora aqui: quem manda na
 * cor e o terminal.
 */
export const FUNDO_DO_TERMINAL = '#011e28'

import { ehAtalhoDoTerminal } from './teclas'

/**
 * O terminal de UMA sessão.
 *
 * Repinta o histórico ao montar: trocar de aba desmonta este componente, e sem
 * repintar a pessoa voltaria para uma tela em branco com a sessão viva — o
 * shell funcionando e o que ela digitou sumido.
 */
export function TerminalDoServidor({
  instanceId,
  historico,
  ouvir,
  digitar,
  redimensionar,
}: {
  instanceId: string
  historico: (id: string) => Uint8Array[]
  /** Registra o ouvinte e devolve a funcao que o REMOVE. */
  ouvir: (id: string, fn: (d: Uint8Array) => void) => () => void
  digitar: (id: string, texto: string) => void
  redimensionar: (id: string, cols: number, lines: number) => void
}) {
  const caixa = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!caixa.current) return

    const term = new Terminal({
      // JetBrains Mono é o monospace do design system (docs/DESIGN_SYSTEM.md).
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontSize: 13,
      cursorBlink: true,
      // Fundo escuro próprio, não a paleta clara do console: terminal é o
      // único lugar da plataforma onde o conteúdo manda na cor, porque quem
      // pinta é o programa remoto.
      theme: { background: FUNDO_DO_TERMINAL, foreground: '#e6f3f5', cursor: '#098d93' },
      convertEol: false,
      scrollback: 10_000,
      // No macOS, Option NAO e Meta por default — sem isto, `Option+Backspace`
      // (apagar palavra) nao gera `ESC DEL` e o atalho simplesmente nao existe
      // para quem usa Mac. No Linux e Windows o Alt ja e Meta.
      macOptionIsMeta: true,
    })
    // Quais teclas o terminal fica e quais o navegador leva. Sem isto,
    // `Ctrl+R` recarrega a pagina no meio de uma busca na historia e `Ctrl+L`
    // pula para a barra de endereco em vez de limpar a tela.
    term.attachCustomKeyEventHandler(ev => {
      if (ehAtalhoDoTerminal(ev)) {
        ev.preventDefault()
        return true
      }
      return true
    })
    const fit = new FitAddon()
    term.loadAddon(fit)
    term.open(caixa.current)
    fit.fit()

    // Histórico primeiro, ao vivo depois — nesta ordem. Invertido, o que
    // chegasse durante o repinte apareceria ANTES do que veio antes dele.
    for (const bloco of historico(instanceId)) term.write(bloco)
    const pararDeOuvir = ouvir(instanceId, dados => term.write(dados))

    const off = term.onData(texto => digitar(instanceId, texto))
    redimensionar(instanceId, term.cols, term.rows)
    const offResize = term.onResize(({ cols, rows }) =>
      redimensionar(instanceId, cols, rows),
    )

    // Foco assim que o terminal aparece — abrir um servidor e ainda ter de
    // clicar dentro da janela para o cursor pegar e um passo que nao serve a
    // ninguem: quem clicou no servidor quer digitar.
    //
    // Vale tambem ao TROCAR de aba, porque este efeito roda de novo por
    // `instanceId`: voltar para uma sessao ja deixa o cursor onde ela esta.
    //
    // O `requestAnimationFrame` existe porque o `fit()` acima remede e
    // reposiciona o textarea escondido do xterm; focar antes disso ora pegava,
    // ora nao, dependendo de o layout ja ter assentado.
    const quadro = requestAnimationFrame(() => term.focus())

    const ao = new ResizeObserver(() => fit.fit())
    ao.observe(caixa.current)

    return () => {
      cancelAnimationFrame(quadro)
      pararDeOuvir()
      off.dispose()
      offResize.dispose()
      ao.disconnect()
      term.dispose()
    }
  }, [instanceId, historico, ouvir, digitar, redimensionar])

  // O respiro fica na MOLDURA, nao no elemento que o xterm mede. O `FitAddon`
  // calcula colunas pela largura do proprio no; padding nele faria a conta
  // sobrar e a ultima coluna cair fora da tela.
  return (
    <div
      style={{
        height: '100%', width: '100%', padding: 8,
        background: FUNDO_DO_TERMINAL, boxSizing: 'border-box',
      }}
    >
      <div ref={caixa} style={{ height: '100%', width: '100%' }} />
    </div>
  )
}
